/**
 * Hook para tags + notas de un contacto externo (cm_contacts).
 *
 * Endpoints reales:
 *   GET  /api/contacts/:clientSlug/:externalUserId
 *   PUT  /api/contacts/:clientSlug/:externalUserId
 *
 * El GET devuelve un default vacío si nunca se persistió el contacto,
 * así que el consumidor siempre puede asumir shape consistente.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { apiGet, apiPut } from "@/lib/api";

export interface Contact {
  id: number | null;
  clientSlug: string;
  externalUserId: string;
  tags: string[];
  notes: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface ApiContact {
  id: number | null;
  clientSlug: string;
  externalUserId: string;
  tags: string[] | null;
  notes: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface ContactKey {
  clientSlug: string;
  externalUserId: string;
}

function pathFor(k: ContactKey): string {
  return `/contacts/${k.clientSlug}/${encodeURIComponent(k.externalUserId)}`;
}

export function useContact(key: ContactKey | null) {
  return useQuery<Contact | null>({
    queryKey: ["contact", key?.clientSlug, key?.externalUserId],
    enabled: !!key,
    queryFn: async () => {
      if (!key) return null;
      const res = await apiGet<{ contact: ApiContact }>(pathFor(key));
      return {
        id: res.contact.id,
        clientSlug: res.contact.clientSlug,
        externalUserId: res.contact.externalUserId,
        tags: res.contact.tags ?? [],
        notes: res.contact.notes,
        updatedAt: res.contact.updatedAt,
        updatedBy: res.contact.updatedBy,
      };
    },
  });
}

interface UpdatePayload {
  key: ContactKey;
  tags?: string[];
  notes?: string | null;
}

export function useUpdateContact() {
  const qc = useQueryClient();
  return useMutation<Contact, Error, UpdatePayload>({
    mutationFn: async ({ key, tags, notes }) => {
      const res = await apiPut<{ contact: ApiContact }>(pathFor(key), {
        ...(tags !== undefined ? { tags } : {}),
        ...(notes !== undefined ? { notes } : {}),
      });
      return {
        id: res.contact.id,
        clientSlug: res.contact.clientSlug,
        externalUserId: res.contact.externalUserId,
        tags: res.contact.tags ?? [],
        notes: res.contact.notes,
        updatedAt: res.contact.updatedAt,
        updatedBy: res.contact.updatedBy,
      };
    },
    onSuccess: (data) => {
      qc.setQueryData(
        ["contact", data.clientSlug, data.externalUserId],
        data,
      );
    },
    onError: (err) => {
      toast.error(`No se pudo guardar: ${err.message}`);
    },
  });
}

/**
 * Sugerencias por defecto. El usuario puede agregar tags custom; los
 * sugeridos solo facilitan la entrada.
 */
export const SUGGESTED_TAGS = [
  "influencer",
  "reclamo",
  "consulta",
  "distribuidor",
  "spam",
  "vip",
] as const;
